// "This and later" on a repeat (7 Oct 2026): before the later visits change
// to match the one being edited or dragged, each is checked at its new time
// against that day's diary, the same way a new repeat is checked. Reads the
// diary (31 days a request, the server's limit), so it works without any
// server change. The pure part is laterClashes; loadSpan does the reading.
import moment from 'moment'
import { hairdresserService } from '../../../_services/hairdresser.service'
import { normalizeDiaryWeekResponse } from '../normalizeData'

const CHUNK_DAYS = 31

// Every day from `from` to `to` for these stylists, a month a request:
// { [staffId]: { days: { date: events[] }, info: { date: { isWorking, startMinutes, endMinutes } } } }
export const loadSpan = async (from, to, staffIds) => {
  const out = {}
  const ids = [...new Set((staffIds || []).filter(Boolean).map(String))]
  ids.forEach((id) => { out[id] = { days: {}, info: {} } })
  const chunks = []
  for (let d = moment(from).startOf('day'); !d.isAfter(to, 'day'); d = moment(d).add(CHUNK_DAYS, 'days')) {
    const end = moment.min(moment(d).add(CHUNK_DAYS - 1, 'days'), moment(to))
    chunks.push([d.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')])
  }
  await Promise.all(ids.flatMap(id => chunks.map(([startDate, endDate]) => hairdresserService.getDiaryData({ startDate, endDate, staffIds: id })
    .then((res) => {
      const data = (res && res.data && (res.data.responseData || res.data.data)) || null
      if (!data || !data.dates) throw new Error('The diary couldn’t be read')
      const { weekData, weekStaffInfo } = normalizeDiaryWeekResponse(data, id)
      Object.assign(out[id].days, weekData)
      Object.assign(out[id].info, weekStaffInfo)
    }))))
  return out
}

const hhmm = m => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`

// The repeat's own visits after this one (one entry per booking, its first
// and last minute across its service segments)
export const laterVisitsOf = (span, seriesId, bookingId, afterDate) => {
  const byId = new Map()
  Object.entries(span || {}).forEach(([staffId, s]) => {
    Object.entries(s.days || {}).forEach(([date, events]) => {
      if (!moment(date).isAfter(afterDate, 'day')) return
      ;(events || []).forEach((e) => {
        if (e.type !== 'booking' || String(e.seriesId) !== String(seriesId) || String(e.bookingId) === String(bookingId)) return
        const id = String(e.bookingId)
        const v = byId.get(id) || { bookingId: id, date, staffId, start: e.startMinutes, end: e.endMinutes }
        v.start = Math.min(v.start, e.startMinutes)
        v.end = Math.max(v.end, e.endMinutes)
        byId.set(id, v)
      })
    })
  })
  return [...byId.values()].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
}

// Each later visit at the new time (and day, if the edit moved the day):
// free, overlapping another booking, or can't be booked (not working, out of
// hours, time off). Shaped like the new-repeat check, so the same screen
// shows it. `move` is { staffId, staffName, start, end, dayShift }.
export const laterClashes = (span, visits, seriesId, move) => {
  const staff = span[String(move.staffId)] || { days: {}, info: {} }
  const name = move.staffName || 'They'
  return visits.map((v) => {
    const date = moment(v.date).add(move.dayShift || 0, 'days').format('YYYY-MM-DD')
    const base = { date, visitId: v.bookingId, fromDate: v.date, oldSlot: [v.start, v.end], oldStaffId: v.staffId }
    const info = staff.info[date]
    if (info && info.isWorking === false) return { ...base, status: 'blocked', reason: `${name} isn’t working` }
    if (info && info.startMinutes != null && info.endMinutes != null && (move.start < info.startMinutes || move.end > info.endMinutes)) {
      return { ...base, status: 'blocked', reason: `Outside ${name}’s hours (${hhmm(info.startMinutes)} to ${hhmm(info.endMinutes)})` }
    }
    const others = (staff.days[date] || []).filter(e => !(e.type === 'booking' && String(e.seriesId) === String(seriesId))
      && move.start < e.endMinutes && move.end > e.startMinutes)
    const away = others.find(e => e.type === 'holiday' || e.type === 'leave')
    if (away) return { ...base, status: 'blocked', reason: `${name} is off (${away.title || 'time off'})` }
    const bookings = others.filter(e => e.type === 'booking')
    const seen = new Set()
    const clients = bookings.filter((e) => { const k = String(e.bookingId); if (seen.has(k)) return false; seen.add(k); return true })
    if (clients.length) {
      const first = clients[0]
      const who = (first.clientName || 'another booking').trim()
      return { ...base, status: 'overlap', reason: clients.length > 1 ? `Overlaps ${who} and ${clients.length - 1} more` : `Overlaps ${who}, ${hhmm(first.startMinutes)} to ${hhmm(first.endMinutes)}` }
    }
    const busy = others.find(e => e.type === 'event' || e.type === 'break')
    if (busy) return { ...base, status: 'overlap', reason: `Overlaps ${busy.title || 'an event'}` }
    return { ...base, status: 'ok' }
  })
}
